import { useTranslation } from 'react-i18next';
import type { UspsModule } from 'types/capi/modules';

import BellOutlineThin from '~/assets/icons/BellOutlineThin';
import ChevronIcon from '~/assets/icons/Chevron';
import ProductTagIcon from '~/assets/icons/ProductTag';
import ResellerIcon from '~/assets/icons/Reseller';
import SneakerIcon from '~/assets/icons/Sneaker';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import useImpressions from '~/hooks/useImpressions';
import useRegion from '~/hooks/useRegion';

type Props = {
  data: UspsModule;
};

export default function Usps({ data }: Props) {
  const region = useRegion();
  const { t } = useTranslation('home');

  const isDefaultRegion = region.code === 'GB';

  const bannerRef = useImpressions([
    {
      bannerId: 'usp-homepage',
      bannerName: 'usp-homepage',
      bannerPosition: 'homepage'
    }
  ]);

  return (
    <div
      ref={bannerRef[0]}
      className="relative flex min-h-[406px] w-full items-center md:min-h-[450px]"
    >
      <BackgroundImage data={data} />
      <div className="flex size-full flex-col gap-7 pt-0 md:mx-auto md:max-w-6xl">
        <div className="relative ml-4 mt-4 text-left text-4xl text-white md:ml-0 md:text-center md:text-5xl">
          {t('whyMillionsOfSneakerheadsTrustUs')}
        </div>
        <div className="custom-scrollbar relative flex items-start gap-4 self-stretch overflow-x-scroll px-4 pb-6 md:gap-6 md:px-0 md:pb-8">
          <div
            className={`flex w-full gap-4 md:grid ${isDefaultRegion ? 'md:grid-cols-4' : 'md:grid-cols-3'} md:gap-6`}
          >
            <Usp
              title={t('uspShop')}
              icon={<SneakerIcon height={24} width={24} stroke="#FFAA00" />}
              link={{
                path: '/release-dates/',
                text: t('uspShopDescription')
              }}
            />
            <Usp
              title={t('uspUpcoming')}
              icon={<BellOutlineThin height="24" width="24" fill="#FFAA00" />}
              link={{
                path: '/release-dates/?sort=UPCOMING_RELEASES',
                text: t('uspUpcomingDescription')
              }}
            />
            <Usp
              title={t('uspDeals')}
              icon={<ProductTagIcon height={24} width={24} stroke="#FFAA00" />}
              link={{
                path: '/release-dates/?sort=SALE',
                text: t('uspDealsDescription')
              }}
            />
            {isDefaultRegion && (
              <Usp
                title="Buy or sell with ease on our marketplace."
                icon={
                  <ResellerIcon
                    height="24"
                    width="24"
                    stroke="#FFAA00"
                    fill="#FFAA00"
                  />
                }
                link={{
                  path: '/sole-market/',
                  text: 'Our community of verified sellers'
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BackgroundImage({ data }: Props) {
  return (
    <>
      <Image
        alt="home-page-usps-image"
        src={data.image_mobile}
        className="absolute inset-0 block size-full object-cover object-left"
        loading="eager"
        responsiveOptions={{
          md: { src: data.image_desktop },
          xs: { height: 672, retainRatio: true, src: data.image_mobile }
        }}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
      />
    </>
  );
}

function Usp({
  title,
  icon,
  link
}: {
  icon: React.ReactNode;
  link: { path: string; text: string };
  title: string;
}) {
  const region = useRegion();

  const isDefaultRegion = region.code === 'GB';

  return (
    <div className="flex h-full w-64 shrink-0 flex-col bg-black/70 px-4 py-6 text-white md:h-auto md:w-full">
      <div className="flex h-full flex-col gap-4 md:gap-6">
        <div>{icon}</div>
        <div
          className={`${isDefaultRegion ? 'text-xl md:text-2xl' : 'text-base md:text-xl'}`}
        >
          {title}
        </div>
        <div className="mt-auto ">
          <Tracked.InternalLink
            pagePosition="Homepage USPs"
            variant="none"
            name="USP_CARD"
            className="flex gap-3 text-xs text-grey-divide md:text-sm"
            to={link.path}
          >
            {link.text}
            <div className="w-3 -rotate-90">
              <ChevronIcon stroke="#FFAA00" width="10" height="10" />
            </div>
          </Tracked.InternalLink>
        </div>
      </div>
    </div>
  );
}
